import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, Button, Space, Image } from 'antd';
import { EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { REPORT_STATUS, REPORT_STATUS_VALUES } from 'constants/reports';
import { setTour } from 'store/tour/slice';
import { useAppDispatch } from 'store/hooks';
import { ColumnProps } from 'antd/lib/table';
import {
  LegacyAppointment,
  fetchAppointments
} from 'services/api/appointments';

import { getIdStart } from 'utils/string';
import { showRestError } from 'utils/showErrorMessage';
import { getFileUrl } from 'utils/file';
import { ViewReport } from 'components/shared';

const PAGE_LIMIT = 10;

const MedicusReports: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const page = Number(searchParams.get('page') || 1);

  const [results, setResults] = useState<LegacyAppointment[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const startDate = searchParams.get('startDate') || undefined;
  const endDate = searchParams.get('endDate') || undefined;
  const name = searchParams.get('name') || undefined;

  useEffect(() => {
    setLoading(true);
    // appointmentsWithReport equivalent: GET /appointments?withReport=1
    fetchAppointments({
      withReport: 1,
      startDate,
      endDate,
      name,
      page,
      limit: PAGE_LIMIT
    })
      .then(res => {
        setResults(res.results);
        setTotal(res.total);
        dispatch(setTour({ key: 'report', value: true }));
      })
      .catch(err => showRestError(err))
      .finally(() => setLoading(false));
  }, [startDate, endDate, name, page, dispatch]);

  const renderActions = useCallback(
    (
      status: string | null | undefined,
      id: string | undefined,
      appt_id: string
    ) => {
      if (status === REPORT_STATUS.sent && id) {
        return <ViewReport id={id} />;
      }

      if (status === REPORT_STATUS.draft) {
        return (
          <Link to={`/report/details/${id}?id=${appt_id}`}>
            <Button
              data-element="report-view-report"
              style={{ minWidth: 135 }}
              icon={<EditOutlined />}
              type="primary"
              ghost
            >
              {t('reports.edit_report')}
            </Button>
          </Link>
        );
      }

      return (
        <Link to={`/report/details?id=${appt_id}`}>
          <Button
            style={{ minWidth: 135 }}
            icon={<PlusOutlined />}
            type="primary"
            ghost
          >
            {t('reports.add_report')}
          </Button>
        </Link>
      );
    },
    [t]
  );

  const columns: ColumnProps<LegacyAppointment>[] = useMemo(
    () => [
      {
        title: t('reports.appointment_id'),
        dataIndex: 'id',
        key: 'id',
        render: (id: string) => getIdStart(id)
      },
      {
        title: t('reports.patient_name'),
        dataIndex: 'report',
        key: 'patient_name',
        render: (_, record) => {
          if (record.report?.first_name || record.report?.last_name) {
            return `${record.report?.first_name} ${record.report?.last_name}`;
          }

          if (record.reports_file) {
            return `${record.patient?.first_name} ${record.patient?.last_name}`;
          }

          return '-';
        }
      },
      {
        title: t('reports.report_type'),
        dataIndex: 'status',
        key: 'status',
        render: (_, record) =>
          record.report?.status
            ? t(`reports.${REPORT_STATUS_VALUES[record.report.status]}`)
            : '-'
      },
      {
        title: t('reports.creation_date'),
        dataIndex: 'creation_date',
        key: 'created_at',
        render: (_, record) => {
          if (record.report?.created_at) {
            return dayjs(record.report.created_at).format(FULL_DATE_FORMAT);
          }

          if (record.reports_file?.created_at) {
            return dayjs(record.reports_file.created_at).format(
              FULL_DATE_FORMAT
            );
          }

          return '-';
        }
      },
      {
        title: t('reports.file'),
        dataIndex: 'file',
        key: 'file',
        render: (_, record) => (
          <span>
            {record.reports_file?.file ? (
              <div>
                <Image
                  src="/images/format-pdf.svg"
                  preview={false}
                  width={24}
                />
                <a
                  href={getFileUrl(record.reports_file?.file)}
                  style={{ marginLeft: 4 }}
                >
                  {record.reports_file?.file}
                </a>
              </div>
            ) : (
              '-'
            )}
          </span>
        )
      },
      {
        title: '',
        dataIndex: 'id',
        key: 'id',
        width: '5%',
        align: 'right',
        render: (id, record) => {
          return (
            <Space>
              {record.report
                ? renderActions(record.report?.status, record.report?.id, id)
                : null}
              <div data-element="report-view-appointment">
                <Link to={`/appointments/details/${id}`}>
                  <Button icon={<EyeOutlined />}>
                    {t('appointments.view_appointment')}
                  </Button>
                </Link>
              </div>
            </Space>
          );
        }
      }
    ],
    [renderActions, t]
  );

  return (
    <Table
      dataSource={results}
      columns={columns}
      loading={loading}
      rowKey="id"
      pagination={{
        total: total || 0,
        current: page,
        onChange: p => {
          searchParams.set('page', `${p}`);
          setSearchParams(searchParams);
        }
      }}
      scroll={{ x: 768 }}
    />
  );
};

export default MedicusReports;
